import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  getViewerPanelPaddingSx,
  viewerPanelSurfaceSx,
} from "./panelStyles";

const GESTURE_HINT_TRANSITION_DELAY_MS = 3000;

export const getGestureHintLabel = (step, isTouchDevice) => {
  switch (step) {
    case "drag":
      return isTouchDevice ? "Drag to look around" : "Click and drag to look around";
    case "tap":
      return isTouchDevice ? "Tap an element to see more" : "Click an element to see more";
    case "zoom":
      return isTouchDevice
        ? "Pinch or tap the zoom button"
        : "Scroll or click the zoom button";
    default:
      return "";
  }
};

const DragCue = () => (
  <Box sx={{ position: "relative", width: 36, height: 28 }}>
    <Box
      sx={{
        position: "absolute",
        left: 5,
        right: 5,
        top: 13,
        height: 1.5,
        background: "var(--recording-cta-bg, #85f5ec)",
        opacity: 0.72,
      }}
    />
    <Box
      sx={{
        position: "absolute",
        left: 2,
        top: 10,
        width: 0,
        height: 0,
        borderTop: "4px solid transparent",
        borderBottom: "4px solid transparent",
        borderRight: "6px solid var(--recording-cta-bg, #85f5ec)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        right: 2,
        top: 10,
        width: 0,
        height: 0,
        borderTop: "4px solid transparent",
        borderBottom: "4px solid transparent",
        borderLeft: "6px solid var(--recording-cta-bg, #85f5ec)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        left: 12,
        top: 8,
        width: 12,
        height: 12,
        borderRadius: "50%",
        border: "2px solid rgba(6, 48, 56, 0.72)",
        background: "#a1f8f1",
      }}
    />
  </Box>
);

const TapCue = () => (
  <Box sx={{ position: "relative", width: 36, height: 28 }}>
    <Box
      sx={{
        position: "absolute",
        left: 8,
        top: 4,
        width: 20,
        height: 20,
        borderRadius: "50%",
        border: "1.5px solid var(--recording-cta-bg, #85f5ec)",
        opacity: 0.72,
        animation: "viewerTutorialTapRipple 1600ms ease-out infinite",
        "@keyframes viewerTutorialTapRipple": {
          "0%": { opacity: 0.78, transform: "scale(0.58)" },
          "70%": { opacity: 0.06, transform: "scale(1.2)" },
          "100%": { opacity: 0, transform: "scale(1.2)" },
        },
      }}
    />
    <Box
      sx={{
        position: "absolute",
        left: 14,
        top: 10,
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: "#a1f8f1",
      }}
    />
  </Box>
);

const ZoomCue = () => (
  <Box sx={{ position: "relative", width: 36, height: 28 }}>
    <Box
      sx={{
        position: "absolute",
        left: 6,
        top: 13,
        width: 24,
        height: 1.5,
        background: "var(--recording-cta-bg, #85f5ec)",
        opacity: 0.72,
        transform: "rotate(-42deg)",
      }}
    />
    {[
      { left: 6, bottom: 2 },
      { right: 6, top: 2 },
    ].map((position, index) => (
      <Box
        key={index}
        sx={{
          position: "absolute",
          width: 10,
          height: 10,
          borderRadius: "50%",
          border: "1.5px solid #a1f8f1",
          background: "rgba(6, 18, 28, 0.88)",
          ...position,
        }}
      />
    ))}
  </Box>
);

export const GestureCue = ({ step }) => {
  if (step === "tap") return <TapCue />;
  if (step === "zoom") return <ZoomCue />;
  return <DragCue />;
};

const ViewerGestureTutorial = ({ step, isPhone = false, isTouchDevice = false }) => {
  const previousStepRef = useRef(step);
  const [visibleStep, setVisibleStep] = useState(step);

  useEffect(() => {
    const previousStep = previousStepRef.current;
    previousStepRef.current = step;

    if (!step) {
      setVisibleStep(null);
      return undefined;
    }

    if (!previousStep || previousStep === step) {
      setVisibleStep(step);
      return undefined;
    }

    setVisibleStep(null);
    const timeoutId = setTimeout(() => {
      setVisibleStep(step);
    }, GESTURE_HINT_TRANSITION_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [step]);

  const renderedStep = visibleStep === step ? visibleStep : null;
  if (!renderedStep) {
    return null;
  }

  return (
    <Box
      data-testid="viewer-gesture-tutorial"
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        top: isPhone ? "54%" : "48%",
        display: "flex",
        justifyContent: "center",
        px: 1.5,
        pointerEvents: "none",
        zIndex: 1999,
      }}
    >
      <Box
        aria-label={getGestureHintLabel(renderedStep, isTouchDevice)}
        role="status"
        sx={{
          ...viewerPanelSurfaceSx,
          ...getViewerPanelPaddingSx(isPhone),
          maxWidth: "min(86vw, 380px)",
          display: "inline-flex",
          alignItems: "center",
          gap: 1.1,
          border: "1px solid rgba(210, 255, 249, 0.22)",
          borderRadius: "999px",
          color: "var(--label-text, #f2fffc)",
        }}
      >
        <GestureCue step={renderedStep} />
        <Typography
          sx={{
            fontSize: isPhone ? "0.84rem" : "0.92rem",
            fontWeight: 650,
            lineHeight: 1.35,
            color: "var(--label-text, #f2fffc)",
            overflowWrap: "anywhere",
          }}
        >
          {getGestureHintLabel(renderedStep, isTouchDevice)}
        </Typography>
      </Box>
    </Box>
  );
};

export default ViewerGestureTutorial;
